/**
 * VerificarEmail — page to validate the 6-digit OTP code sent after register.
 *
 * Flow:
 *  - Email arrives from location.state (set by Cadastrar.js after register)
 *    or from localStorage (persists across reloads).
 *  - User enters OTP → POST /api/auth/verify-email
 *  - On success: navigate to /cadastrar with success toast.
 *  - Resend available after 60s countdown via POST /api/auth/send-verification-email.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Smartphone, CheckCircle2, Loader2, RotateCcw, AlertCircle } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Button } from '../components/ui/button';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

const RESEND_SECONDS = 60;

export default function VerificarEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth() || {};

  const initialEmail = useMemo(() => {
    return (
      location?.state?.email ||
      new URLSearchParams(location.search).get('email') ||
      localStorage.getItem('pending_verification_email') ||
      ''
    );
  }, [location]);

  const [email] = useState(initialEmail);
  // Canal persiste no localStorage: no celular, sair do navegador para ver o
  // SMS descarta a página — ao voltar, o código era validado no canal errado.
  const [channel, setChannel] = useState(() => {
    try { return localStorage.getItem('pending_verification_channel') || 'email'; } catch { return 'email'; }
  });
  const [phoneMasked, setPhoneMasked] = useState(() => {
    try { return localStorage.getItem('pending_verification_phone') || ''; } catch { return ''; }
  });
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_SECONDS);
  const [error, setError] = useState('');
  // `verifyGate` bloqueia render + auto-send até termos a resposta do /auth/me.
  //   'checking' → aguardando resposta do backend (spinner)
  //   'needs_verify' → precisa mesmo verificar (mostra o form)
  //   'already_verified' → redireciona (evita mostrar OTP + evita auto-send)
  //   'unknown' → deslogado / 401 (segue o fluxo normal de OTP)
  const [verifyGate, setVerifyGate] = useState('checking');
  const cooldownRef = useRef(null);

  // If no email context at all → redirect to login (nothing to verify)
  useEffect(() => {
    if (!email) {
      toast.error('Faça login ou cadastre-se primeiro.');
      navigate('/cadastrar', { replace: true });
    }
  }, [email, navigate]);

  // GUARD: se o e-mail já está verificado, não mostrar essa página nem
  // disparar novo OTP. Vai direto para a home. Consulta o /auth/me pra
  // pegar o estado real do backend (não confia só no localStorage/cache).
  // O gate `verifyGate` também bloqueia o auto-send abaixo — evita disparar
  // OTP para quem já está verificado (v37 - Auto-Verify Skip).
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data: me } = await api.get('/auth/me');
        if (cancel) return;
        // Considera "já verificado" quando o canal atual está OK.
        // Para SMS exige telefone_verificado; para e-mail exige email_verificado.
        const smsOk = me?.telefone_verificado === true;
        const emailOk = me?.email_verificado === true;
        const alreadyOk = channel === 'sms' ? (smsOk || emailOk) : emailOk;
        if (alreadyOk) {
          try {
            localStorage.removeItem('pending_verification_email');
            localStorage.removeItem('pending_verification_channel');
            localStorage.removeItem('pending_verification_phone');
          } catch (_) { /* ignore */ }
          setVerifyGate('already_verified');
          toast.success(channel === 'sms' && smsOk && !emailOk
            ? 'Seu telefone já está verificado.'
            : 'Seu e-mail já está verificado.');
          navigate('/', { replace: true });
          return;
        }
        setVerifyGate('needs_verify');
      } catch (_) {
        if (!cancel) setVerifyGate('unknown');
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  // Persist email for reload resilience
  useEffect(() => {
    if (email) localStorage.setItem('pending_verification_email', email);
  }, [email]);

  // Auto-envio: quando o usuário chega pelo banner "Verificar agora" (ou pelo
  // redirect do interceptor com ?send=1), o código original já expirou — sem
  // isso ele fica esperando um e-mail que nunca foi disparado.
  //
  // IMPORTANTE (v37): só dispara APÓS o `verifyGate` confirmar que o usuário
  // realmente precisa verificar. Isso evita SMS/e-mail duplicado quando quem
  // já verificou volta para a página (ex.: ao clicar em link antigo).
  const autoSentRef = useRef(false);
  useEffect(() => {
    const wantsAutoSend = location?.state?.autoSend ||
      new URLSearchParams(location.search).get('send') === '1';
    if (!email || !wantsAutoSend || autoSentRef.current) return;
    if (verifyGate !== 'needs_verify' && verifyGate !== 'unknown') return;
    autoSentRef.current = true;
    (async () => {
      try {
        const endpoint = channel === 'sms' ? '/auth/send-verification-sms' : '/auth/send-verification-email';
        const { data } = await api.post(endpoint, { email });
        if (channel === 'sms' && data?.phone_masked) setPhoneMasked(data.phone_masked);
        toast.success(channel === 'sms' ? 'Código enviado por SMS.' : 'Código enviado para seu e-mail.');
        setCooldown(RESEND_SECONDS);
      } catch (_) { /* já verificado ou rate limit — o usuário pode reenviar */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, verifyGate]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(cooldownRef.current);
  }, [cooldown]);

  const handleVerify = async (codigoOverride) => {
    const codigo = (codigoOverride ?? otp).trim();
    if (codigo.length !== 6) {
      setError('Digite os 6 dígitos do código.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const endpoint = channel === 'sms' ? '/auth/verify-phone' : '/auth/verify-email';
      await api.post(endpoint, { email, codigo });
      // Recarrega user do /auth/me para que email_verificado/telefone_verificado
      // sejam atualizados no AuthContext. Sem isso o EmailVerificationBanner (e
      // outros gates que dependem de user.email_verificado) continuavam
      // mostrando o aviso de "verifique seu e-mail" mesmo após verificação —
      // usuário reportou como bug em prod (v35+).
      try { await refreshUser?.(); } catch (_) { /* segue o fluxo */ }
      toast.success(
        channel === 'sms'
          ? 'Telefone verificado! Sua conta foi ativada.'
          : 'E-mail verificado! Conta ativa.'
      );
      localStorage.removeItem('pending_verification_email');
      try {
        localStorage.removeItem('pending_verification_channel');
        localStorage.removeItem('pending_verification_phone');
        localStorage.removeItem('email_verify_skipped');
      } catch (_) { /* ignore */ }
      // Resume intent saved by AuthModal: empreendedor → wizard | consumidor → home
      let intent = null;
      try {
        const raw = sessionStorage.getItem('post_verify_intent');
        intent = raw ? JSON.parse(raw) : null;
        sessionStorage.removeItem('post_verify_intent');
      } catch (_) { /* ignore */ }
      if (intent?.flow === 'empreendedor') {
        const qs = intent.municipio_id
          ? `?intent=empreendedor&municipio_id=${encodeURIComponent(intent.municipio_id)}`
          : '?intent=empreendedor';
        navigate(`/cadastrar${qs}`, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Código inválido ou expirado';
      setError(detail);
      setOtp('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setError('');
    try {
      const endpoint = channel === 'sms' ? '/auth/send-verification-sms' : '/auth/send-verification-email';
      const { data } = await api.post(endpoint, { email });
      if (channel === 'sms' && data?.phone_masked) setPhoneMasked(data.phone_masked);
      toast.success(channel === 'sms' ? 'Código reenviado por SMS.' : 'Código reenviado para seu e-mail.');
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Não foi possível reenviar o código.';
      toast.error(detail);
    } finally {
      setResending(false);
    }
  };

  const switchChannel = async (next) => {
    if (next === channel || switching) return;
    setSwitching(true);
    setError('');
    setOtp('');
    try {
      if (next === 'sms') {
        const { data } = await api.post('/auth/send-verification-sms', { email });
        if (data?.phone_masked) {
          setPhoneMasked(data.phone_masked);
          try { localStorage.setItem('pending_verification_phone', data.phone_masked); } catch (_) { /* ignore */ }
        }
        toast.success(`Código enviado para ${data?.phone_masked || 'seu telefone'}.`);
      } else {
        await api.post('/auth/send-verification-email', { email });
        toast.success('Código enviado para seu e-mail.');
      }
      setChannel(next);
      try { localStorage.setItem('pending_verification_channel', next); } catch (_) { /* ignore */ }
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Não foi possível enviar o código.';
      toast.error(detail);
    } finally {
      setSwitching(false);
    }
  };

  // Auto-submit when all 6 digits are entered
  useEffect(() => {
    if (otp.length === 6 && !submitting) {
      handleVerify(otp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const isSMS = channel === 'sms';

  // Enquanto o /auth/me não responder, mostra um spinner leve — evita o
  // "flash" do formulário para usuários que já estão verificados e serão
  // redirecionados. Também previne o botão "Reenviar" de piscar habilitado.
  if (verifyGate === 'checking' || verifyGate === 'already_verified') {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-pink-50 px-4 py-12"
        data-testid="verificar-email-page-checking"
      >
        <div className="flex flex-col items-center gap-3 text-gray-600">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          <p className="text-sm">Verificando seu cadastro…</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-pink-50 px-4 py-12"
      data-testid="verificar-email-page"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 sm:p-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
            {isSMS ? <Smartphone className="w-8 h-8 text-purple-600" /> : <Mail className="w-8 h-8 text-purple-600" />}
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            {isSMS ? 'Verifique seu telefone' : 'Verifique seu e-mail'}
          </h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            Enviamos um código de verificação para{' '}
            <strong className="text-gray-900" data-testid="verify-email-address">
              {isSMS ? (phoneMasked || 'seu telefone cadastrado') : email}
            </strong>.
            <br />
            Insira o código abaixo para ativar sua conta.
          </p>
        </div>

        {/* Channel tabs */}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-5" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={!isSMS}
            disabled={switching || submitting}
            onClick={() => switchChannel('email')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
              !isSMS ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid="verify-channel-email"
          >
            <Mail className="w-4 h-4" /> Por e-mail
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isSMS}
            disabled={switching || submitting}
            onClick={() => switchChannel('sms')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
              isSMS ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid="verify-channel-sms"
          >
            <Smartphone className="w-4 h-4" /> Por SMS
          </button>
        </div>

        <div className="flex flex-col items-center gap-3">
          <InputOTP
            maxLength={6}
            value={otp}
            onChange={(v) => {
              setOtp(v);
              if (error) setError('');
            }}
            disabled={submitting || switching}
            data-testid="verify-otp-input"
            autoFocus
            inputMode="numeric"
          >
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot key={i} index={i} className="w-11 h-12 sm:w-12 sm:h-14 text-lg" />
              ))}
            </InputOTPGroup>
          </InputOTP>

          {error && (
            <p
              className="text-sm text-red-600 flex items-center gap-1.5 mt-1"
              data-testid="verify-error"
            >
              <AlertCircle className="w-4 h-4" /> {error}
            </p>
          )}
        </div>

        <Button
          className="w-full mt-6 bg-purple-600 hover:bg-purple-700 h-11"
          onClick={() => handleVerify()}
          disabled={submitting || otp.length !== 6}
          data-testid="verify-submit-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verificando...
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" /> Verificar e acessar minha conta
            </>
          )}
        </Button>

        <div className="mt-6 text-center text-sm text-gray-600">
          Não recebeu o código?{' '}
          {cooldown > 0 ? (
            <span className="text-gray-400" data-testid="resend-countdown">
              Reenviar em {cooldown}s
            </span>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="text-purple-600 hover:text-purple-700 font-semibold disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="verify-resend-btn"
            >
              {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              Reenviar código
            </button>
          )}
        </div>

        <div className="mt-4 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => {
              // Opt-in para o modo "leve": interceptor mostra toast em vez de
              // redirecionar; usuário pode navegar e voltar quando quiser via banner.
              try { localStorage.setItem('email_verify_skipped', '1'); } catch (_) { /* ignore */ }
              navigate('/', { replace: true });
            }}
            className="text-xs text-gray-600 hover:text-purple-700 underline font-medium"
            data-testid="verify-skip-btn"
          >
            Verificar depois
          </button>
          <span className="text-gray-300">·</span>
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem('pending_verification_email');
              navigate('/cadastrar');
            }}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
            data-testid="verify-change-account-btn"
          >
            Usar outra conta
          </button>
        </div>
      </div>
    </div>
  );
}
